'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Property, PropertyFilters } from '@/lib/api/clients/properties';
import { PropertyGrid } from './PropertyGrid';
import { PropertyListCard } from './PropertyListCard';
import { ViewSwitcher, type ViewMode } from './ViewSwitcher';

export interface PropertyListingRenderProps {
  properties: Property[];
  isLoading: boolean;
  error: string | undefined;
  refetch: () => Promise<void>;
  viewMode: ViewMode;
  setViewMode: (view: ViewMode) => void;
  organisationId: string;
  locale: string;
}

export interface PropertyListingProps {
  organisationId: string;
  locale?: string;
  /**
   * Filters passed directly to the Properties API
   */
  filters?: PropertyFilters;
  /**
   * Max results to request from the API
   */
  limit?: number;
  /**
   * Include related entities (images, attributes, etc.)
   * Defaults to true so themes get everything they need for display
   */
  includeRelations?: boolean;
  /**
   * Server-provided properties for instant render/SSR
   */
  initialProperties?: Property[];
  /**
   * Fallback properties used when the API is unreachable
   */
  fallbackProperties?: Property[];
  /**
   * Optional render prop for themes to take over the UI
   */
  render?: (props: PropertyListingRenderProps) => ReactNode;
  /**
   * Alias for render for convenience
   */
  children?: (props: PropertyListingRenderProps) => ReactNode;
  /**
   * Default empty/error/loading messages for the built-in renderer
   */
  emptyMessage?: string;
  loadingMessage?: string;
  errorMessage?: string;
  /**
   * Default view to show in the built-in renderer
   */
  initialView?: ViewMode;
  /**
   * Re-fetch properties on mount from the client side.
   * Defaults to false when initialProperties are provided (server already fetched),
   * and true otherwise. Set explicitly to override this behavior.
   * 
   * Note: Client-side fetches may fail if the API requires server-side authentication
   * (e.g., service tokens). For SSR pages, prefer passing initialProperties and
   * letting the server handle data fetching with proper credentials.
   */
  refreshOnMount?: boolean;
}

export function PropertyListing({
  organisationId,
  locale = 'en',
  filters,
  limit,
  includeRelations = true,
  initialProperties = [],
  fallbackProperties = [],
  render,
  children,
  emptyMessage = 'No properties available.',
  loadingMessage = 'Loading properties…',
  errorMessage = 'Unable to load properties right now.',
  initialView = 'grid',
  // Skip refetch by default if we already have initialProperties from the server
  refreshOnMount = !initialProperties?.length,
}: PropertyListingProps) {
  const [properties, setProperties] = useState<Property[]>(initialProperties.length ? initialProperties : fallbackProperties);
  const [isLoading, setIsLoading] = useState<boolean>(refreshOnMount);
  const [error, setError] = useState<string | undefined>();
  const [viewMode, setViewMode] = useState<ViewMode>(initialView);

  // Sync initialProperties with state when they change (e.g., client-side navigation)
  // This ensures the component updates when the server provides new data
  useEffect(() => {
    if (initialProperties.length > 0) {
      setProperties(initialProperties);
      setError(undefined);
    }
  }, [initialProperties]);

  const appliedFilters = useMemo<PropertyFilters>(
    () => ({
      status: filters?.status || ['ACTIVE'],
      includeRelations,
      ...(limit !== undefined && { limit }),
      ...filters,
    }),
    [filters, includeRelations, limit]
  );

  const fetchProperties = useCallback(async () => {
    if (!organisationId) {
      setError('Missing organisationId');
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      const resp = await fetch('/api/properties/list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organisationId, filters: appliedFilters }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const { data } = await resp.json() as { data: Property[] };
      setProperties(data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);

      // Fallback to provided properties so themes still have something to render
      if (fallbackProperties.length && !properties.length) {
        setProperties(fallbackProperties);
      }
    } finally {
      setIsLoading(false);
    }
  }, [organisationId, appliedFilters, fallbackProperties.length, properties.length]);

  useEffect(() => {
    if (!refreshOnMount) return;
    void fetchProperties();
  }, [fetchProperties, refreshOnMount]);

  const renderer = render || children;

  const renderProps: PropertyListingRenderProps = {
    properties,
    isLoading,
    error,
    refetch: fetchProperties,
    viewMode,
    setViewMode,
    organisationId,
    locale,
  };

  if (renderer) {
    return <>{renderer(renderProps)}</>;
  }

  return (
    <DefaultPropertyListing
      {...renderProps}
      emptyMessage={emptyMessage}
      loadingMessage={loadingMessage}
      errorMessage={errorMessage}
    />
  );
}

interface DefaultRendererProps extends PropertyListingRenderProps {
  emptyMessage: string;
  loadingMessage: string;
  errorMessage: string;
}

function DefaultPropertyListing({
  properties,
  isLoading,
  error,
  refetch,
  viewMode,
  setViewMode,
  locale,
  emptyMessage,
  loadingMessage,
  errorMessage,
}: DefaultRendererProps) {
  const hasContent = properties.length > 0;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Properties</h2>
          <p className="text-sm text-gray-600">Live listings for this organisation</p>
        </div>
        <ViewSwitcher
          activeView={viewMode}
          onViewChange={setViewMode}
          className="bg-gray-50"
          showLabels
        />
      </div>

      {isLoading && !hasContent ? (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 shadow-sm">
          <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
          {loadingMessage}
        </div>
      ) : null}

      {error && !hasContent ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-semibold">{errorMessage}</p>
          <p className="mt-1 text-amber-700">{error}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : null}

      {!isLoading && !error && !hasContent ? (
        <div className="rounded-lg border border-gray-200 bg-white px-6 py-10 text-center shadow-sm">
          <p className="text-lg font-semibold text-gray-900">{emptyMessage}</p>
          <p className="mt-2 text-sm text-gray-600">Try adjusting your filters or check back soon.</p>
        </div>
      ) : null}

      {hasContent && (
        <div className="mt-6">
          {viewMode === 'list' ? (
            <div className="space-y-4">
              {properties.map((property) => (
                <PropertyListCard
                  key={property.id}
                  property={property}
                  href={`/${locale}/properties/${property.id}`}
                  showActions={false}
                />
              ))}
            </div>
          ) : viewMode === 'map' ? (
            <div className="rounded-lg border border-dashed border-gray-200 bg-white px-6 py-8 text-center text-gray-600">
              Map view is not implemented in the default renderer. Provide a render prop to supply your own map experience.
            </div>
          ) : (
            <PropertyGrid
              properties={properties}
              columns={3}
              gap="lg"
            />
          )}
        </div>
      )}
    </section>
  );
}

